/**
 * PptxEditorInline — text-editing surface for .pptx files.
 *
 * Sibling to `DocxEditorInline` / `XlsxEditorInline`. Closes the V2 parity
 * gap with V1 (which mounted ONLYOFFICE for slide editing). V2 had only
 * `PptxViewerInline` (read-only PDF preview) for non-marp decks; this
 * primitive lets the user type into the actual OOXML text runs of an
 * uploaded `.pptx` without leaving the artifact pane.
 *
 * Why text-only (instead of full WYSIWYG):
 *  - The OSS slide-editor space is barren in mid-2026 — Univer Slides has
 *    an empty facade, OnlyOffice ships a ribbon-mode SaaS only (not an
 *    embeddable primitive), and pptx-genjs / pptx-automizer are NodeJS
 *    GENERATORS, not browser editors. Building a full WYSIWYG slide canvas
 *    is a quarter-long effort and we have shipping-quality alternatives:
 *      • marp-generated decks → `MarpDeckEditor` (already shipped)
 *      • AI-driven mutations  → `SlideDeckProposalMount` (already shipped)
 *  - The remaining gap is "user uploads `report.pptx`, wants to fix a
 *    typo / update a number / tweak a bullet" — 90% of which is TEXT
 *    edits to existing `<a:t>` runs. This primitive solves exactly that
 *    surface area with deterministic byte-identity on save (no layout
 *    rewrite, no style loss — we only touch the inner text nodes).
 *  - Anything richer than text (insert shape, change colour) routes to
 *    the AI HITL flow → already first-class via the proposal pipeline.
 *
 * Tech choice — jszip:
 *  - `.pptx` is a zip. We parse client-side with jszip (~96KB, MIT,
 *    already transitively in the workspace via the xlsx package's
 *    sibling tree). DOMParser (browser-native) walks the slide XML to
 *    extract `<a:t>` runs. Save flips the runs back and re-zips with
 *    JSZip.generateAsync. Round-trip preserves every other slide artefact
 *    (theme, master, images, animations) because we only mutate text
 *    node values, not structure.
 *
 * Local-first contract — identical to Docx/Xlsx siblings:
 *  - First mount fetches `session.read_url` once; the parsed pptx +
 *    edited text live in component state.
 *  - On every keystroke we mark `dirty` + debounce a draft snapshot to
 *    IndexedDB (`saveDraft`) — survives lease expiry / tab crash.
 *  - "Salva" is the ONLY path that talks upstream (`onSave(blob)`).
 *    Autosave NEVER hits MinIO/Drive — this is the load-bearing rule
 *    the docx/xlsx primitives also enforce.
 */

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import JSZip from "jszip";

import { Button } from "../primitives/button";
import { cn } from "../lib/cn";
import type { EditSession } from "./EditSession";
import { fetchSessionBytes } from "./fetchSessionBytes";
import { saveDraft } from "./persistence";

/** Canonical PPTX MIME — kept here so onSave consumers don't reinvent it. */
const PPTX_MIME =
  "application/vnd.openxmlformats-officedocument.presentationml.presentation";

/** ms of inactivity before we snapshot to IndexedDB. */
const DRAFT_DEBOUNCE_MS = 800;

/** OOXML drawingml namespace — anchors the `<a:t>` text-run lookup. */
const DRAWINGML_NS = "http://schemas.openxmlformats.org/drawingml/2006/main";

/**
 * One editable text run inside a slide. The OOXML hierarchy is
 * `<p:sp>` (shape) → `<p:txBody>` → `<a:p>` (paragraph) → `<a:r>` (run) →
 * `<a:t>` (text). We surface one entry per `<a:t>` element so the user
 * gets a `textarea` per styled span; merging would lose style boundaries.
 */
interface PptxTextRun {
  /** Stable id (slide index + run index — survives re-parse). */
  readonly id: string;
  /** Current text value (edited locally). */
  text: string;
  /** Reference to the live DOM node so we can write back on serialise. */
  readonly node: Text;
}

interface PptxSlideModel {
  /** 1-indexed slide number (also doubles as React key). */
  readonly index: number;
  /** Internal path inside the zip, e.g. `ppt/slides/slide1.xml`. */
  readonly path: string;
  /** Parsed XML document — kept live so edits flow into it via Text nodes. */
  readonly doc: XMLDocument;
  /** Text runs sorted in document order. */
  readonly runs: PptxTextRun[];
}

interface PptxModel {
  readonly zip: JSZip;
  readonly slides: PptxSlideModel[];
}

/**
 * Sort the slide entries inside the pptx zip in numeric order. The zip
 * lists them lexicographically by default (`slide1, slide10, slide2…`),
 * which would put slide 10 before slide 2 in the UI. We extract the
 * trailing integer and sort ascending so the rail matches PowerPoint.
 */
function compareSlidePaths(a: string, b: string): number {
  const ra = /slide(\d+)\.xml$/.exec(a);
  const rb = /slide(\d+)\.xml$/.exec(b);
  const na = ra ? Number(ra[1]) : 0;
  const nb = rb ? Number(rb[1]) : 0;
  return na - nb;
}

/**
 * Parse a `.pptx` zip into a slide model. Throws if the file isn't a
 * valid pptx (no `ppt/slides/*.xml` entries). The caller surfaces the
 * error in the editor banner so the user can re-upload.
 */
async function parsePptx(buffer: ArrayBuffer): Promise<PptxModel> {
  const zip = await JSZip.loadAsync(buffer);
  const slidePaths: string[] = [];
  zip.forEach((relativePath) => {
    if (/^ppt\/slides\/slide\d+\.xml$/.test(relativePath)) {
      slidePaths.push(relativePath);
    }
  });
  slidePaths.sort(compareSlidePaths);
  if (slidePaths.length === 0) {
    throw new Error("Nessuna slide trovata nel file .pptx");
  }
  const parser = new DOMParser();
  const slides: PptxSlideModel[] = [];
  for (let i = 0; i < slidePaths.length; i += 1) {
    const path = slidePaths[i] as string;
    const file = zip.file(path);
    if (!file) continue;
    const xmlText = await file.async("string");
    const doc = parser.parseFromString(xmlText, "application/xml");
    // Defensive: a malformed XML body surfaces a `<parsererror>` child
    // (browser-dependent). Skip the slide rather than crashing the whole
    // deck — the user can still edit the other slides.
    if (doc.getElementsByTagName("parsererror").length > 0) {
      slides.push({ index: i + 1, path, doc, runs: [] });
      continue;
    }
    const runs: PptxTextRun[] = [];
    const aTNodes = doc.getElementsByTagNameNS(DRAWINGML_NS, "t");
    for (let r = 0; r < aTNodes.length; r += 1) {
      const el = aTNodes.item(r);
      if (!el) continue;
      // Ensure the element has a single Text child we can rewrite. An
      // empty `<a:t/>` element gets one synthesised so typing into the
      // textarea is reflected on serialise.
      let textNode: Text | null = null;
      for (let c = 0; c < el.childNodes.length; c += 1) {
        const child = el.childNodes.item(c);
        if (child && child.nodeType === 3 /* TEXT_NODE */) {
          textNode = child as Text;
          break;
        }
      }
      if (!textNode) {
        textNode = doc.createTextNode("");
        el.appendChild(textNode);
      }
      runs.push({
        id: `s${i + 1}:r${r}`,
        text: textNode.nodeValue ?? "",
        node: textNode,
      });
    }
    slides.push({ index: i + 1, path, doc, runs });
  }
  return { zip, slides };
}

/**
 * Serialise a {@link PptxModel} back to .pptx bytes. We mutate the
 * underlying Text nodes (already done by the caller before calling this
 * function) and re-write each modified slide XML into the original zip.
 * Returns a fresh Uint8Array suitable for `new Blob([…])`.
 */
async function serialisePptx(model: PptxModel): Promise<Uint8Array> {
  const serializer = new XMLSerializer();
  for (const slide of model.slides) {
    const xml = serializer.serializeToString(slide.doc);
    model.zip.file(slide.path, xml);
  }
  return model.zip.generateAsync({
    type: "uint8array",
    compression: "DEFLATE",
    compressionOptions: { level: 6 },
  });
}

export interface PptxEditorInlineProps {
  readonly session: EditSession;
  /**
   * Upstream save handler. Promise resolution clears `dirty_since`;
   * rejection keeps the local draft. Identical to the Docx/Xlsx
   * contract — caller decides transport (MinIO PUT, Drive REST, …).
   */
  readonly onSave?: (blob: Blob) => Promise<void>;
  readonly className?: string;
}

/**
 * Imperative handle — matches the Docx/Xlsx primitives so the chat HITL
 * bridge can call `flush()` symmetrically. AI proposals route through the
 * existing `proposalsStore`; we don't expose `applyAIProposal` yet because
 * no BE sender emits a `pptx_set_text` proposal kind today (the proposal
 * surface for pptx is the SlideDeckProposalMount + bbox overlays). Will
 * be added when `proposalsStore` ships the `pptx_set_text` discriminator.
 */
export interface PptxEditorInlineHandle {
  /** Snapshot the live deck and persist it as a draft + invoke onSave. */
  flush: () => Promise<void>;
}

export const PptxEditorInline = forwardRef<
  PptxEditorInlineHandle,
  PptxEditorInlineProps
>(function PptxEditorInline({ session, onSave, className }, ref) {
  const { i18n } = useLingui();
  const [model, setModel] = useState<PptxModel | null>(null);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeSlide, setActiveSlide] = useState(1);
  /** Re-render bumper — incremented when a run mutates so derived views
   *  (thumbnail text preview) refresh without us re-cloning the model. */
  const [, bumpVersion] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Boot: fetch + parse the .pptx bytes ─────────────────────────────────
  useEffect(() => {
    const ctrl = new AbortController();
    let cancelled = false;
    setModel(null);
    setLoadError(null);
    setActiveSlide(1);
    (async () => {
      try {
        const buf = await fetchSessionBytes(session.read_url, {
          signal: ctrl.signal,
        });
        const parsed = await parsePptx(buf);
        if (!cancelled) setModel(parsed);
      } catch (err) {
        if ((err as { name?: string }).name === "AbortError") return;
        if (!cancelled) {
          setLoadError(err instanceof Error ? err : new Error(String(err)));
        }
      }
    })();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [session.read_url]);

  // ── IDB-local draft snapshot (autosave NEVER hits upstream) ─────────────
  const persistDraft = useCallback(async (): Promise<Blob | null> => {
    if (!model) return null;
    let bytes: Uint8Array;
    try {
      bytes = await serialisePptx(model);
    } catch (err) {
      console.warn("[PptxEditorInline] failed to serialise pptx", err);
      return null;
    }
    // Copy into a fresh ArrayBuffer — same SharedArrayBuffer typing
    // workaround as XlsxEditorInline (BlobPart requires ArrayBuffer-only).
    const ab = new ArrayBuffer(bytes.byteLength);
    new Uint8Array(ab).set(bytes);
    const blob = new Blob([ab], { type: PPTX_MIME });
    await saveDraft({
      session_id: session.session_id,
      doc_id: session.doc_id,
      mime_type: session.mime_type,
      blob,
      base_etag: session.etag,
      dirty_since: Date.now(),
      ai_proposals: [],
    });
    return blob;
  }, [
    model,
    session.session_id,
    session.doc_id,
    session.mime_type,
    session.etag,
  ]);

  const scheduleDraft = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void persistDraft();
    }, DRAFT_DEBOUNCE_MS);
  }, [persistDraft]);

  // ── flush (Salva click) — only path that talks upstream ─────────────────
  const flush = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    try {
      const blob = await persistDraft();
      if (blob && onSave) await onSave(blob);
      if (blob) {
        await saveDraft({
          session_id: session.session_id,
          doc_id: session.doc_id,
          mime_type: session.mime_type,
          blob,
          base_etag: session.etag,
          dirty_since: null,
          ai_proposals: [],
        });
      }
      setDirty(false);
    } finally {
      setSaving(false);
    }
  }, [
    onSave,
    persistDraft,
    saving,
    session.doc_id,
    session.etag,
    session.mime_type,
    session.session_id,
  ]);

  useImperativeHandle(ref, () => ({ flush }), [flush]);

  // Cleanup the pending debounce on unmount so we don't snapshot stale
  // bytes after a session swap.
  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  // ── Run editor handlers ────────────────────────────────────────────────
  const handleRunChange = useCallback(
    (run: PptxTextRun, next: string) => {
      run.text = next;
      run.node.nodeValue = next;
      setDirty(true);
      bumpVersion((v) => v + 1);
      scheduleDraft();
    },
    [scheduleDraft],
  );

  // Keyboard navigation between slides (↑/↓ when focus is in the rail).
  const onRailKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!model) return;
      const total = model.slides.length;
      if (event.key === "ArrowDown" || event.key === "ArrowRight") {
        event.preventDefault();
        setActiveSlide((n) => Math.min(total, n + 1));
      } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
        event.preventDefault();
        setActiveSlide((n) => Math.max(1, n - 1));
      }
    },
    [model],
  );

  // Derive the currently-shown slide.
  const slide = useMemo(() => {
    if (!model) return null;
    return model.slides.find((s) => s.index === activeSlide) ?? model.slides[0] ?? null;
  }, [model, activeSlide]);

  return (
    <div className={cn("flex h-full w-full flex-col bg-cream", className)}>
      {/* Toolbar — mirrors Xlsx/Docx chrome row (dirty pill + Salva CTA) */}
      <div className="flex h-9 shrink-0 items-center justify-between gap-2 border-hairline border-b px-3">
        <div className="flex items-center gap-2 text-[11px] text-ink-3">
          {model ? (
            <span className="tabular-nums">
              {model.slides.length}{" "}
              {model.slides.length === 1
                ? i18n._(msg`slide`)
                : i18n._(msg`slide`)}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {dirty ? (
            <span className="text-[11px] text-ando-orange" aria-live="polite">
              {i18n._(msg`Modifiche non salvate`)}
            </span>
          ) : (
            <span className="hidden text-[11px] text-ink-3 lg:inline">
              {i18n._(msg`Scrivi qui o chiedi ad Ando`)}
            </span>
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={() => void flush()}
            disabled={saving || !model}
          >
            {saving ? i18n._(msg`Salvataggio…`) : i18n._(msg`Salva`)}
          </Button>
        </div>
      </div>

      {/* Body — left thumbnail rail + right editing pane */}
      <div className="flex min-h-0 flex-1">
        {loadError ? (
          <div className="px-6 py-10 text-ink-3 text-sm">
            {i18n._(msg`Impossibile aprire la presentazione.`)} ({loadError.message})
          </div>
        ) : !model ? (
          <div className="px-6 py-10 text-ink-3 text-sm">
            {i18n._(msg`Caricamento presentazione…`)}
          </div>
        ) : (
          <>
            <nav
              aria-label={i18n._(msg`Anteprime slide`)}
              onKeyDown={onRailKeyDown}
              className="flex shrink-0 flex-col gap-1.5 overflow-y-auto border-hairline border-r bg-cream-50/60 py-2"
              style={{ width: 196 }}
            >
              {model.slides.map((s) => (
                <SlideThumb
                  key={s.index}
                  slide={s}
                  active={s.index === activeSlide}
                  onClick={() => setActiveSlide(s.index)}
                />
              ))}
            </nav>
            <section
              aria-label={i18n._(msg`Slide ${activeSlide}`)}
              className="flex min-w-0 flex-1 flex-col overflow-y-auto px-6 py-6"
            >
              {slide ? (
                <SlideEditor slide={slide} onRunChange={handleRunChange} />
              ) : null}
            </section>
          </>
        )}
      </div>
    </div>
  );
});

PptxEditorInline.displayName = "PptxEditorInline";

// ─── Thumbnail rail entry ───────────────────────────────────────────────

const SlideThumb: React.FC<{
  slide: PptxSlideModel;
  active: boolean;
  onClick: () => void;
}> = ({ slide, active, onClick }) => {
  const { i18n } = useLingui();
  // Build a tiny text preview from the first two non-empty runs (title +
  // first bullet). Keeps the rail useful when the user has 30 slides.
  const preview = useMemo(() => {
    const lines: string[] = [];
    for (const run of slide.runs) {
      const trimmed = run.text.trim();
      if (trimmed.length === 0) continue;
      lines.push(trimmed);
      if (lines.length >= 2) break;
    }
    return lines;
  }, [slide.runs]);
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "true" : undefined}
      aria-label={i18n._(msg`Slide ${slide.index}`)}
      className={cn(
        "mx-2 flex flex-col gap-1 rounded-md border bg-white px-2 py-1.5 text-left transition-colors",
        active
          ? "border-ando-orange ring-2 ring-ando-orange/30"
          : "border-hairline hover:border-ando-orange/50",
      )}
    >
      <span
        className={cn(
          "font-medium text-[10px] tabular-nums",
          active ? "text-ando-orange" : "text-ink-3",
        )}
      >
        {slide.index}
      </span>
      {preview.length > 0 ? (
        <div className="space-y-0.5">
          {preview.map((line, i) => (
            <p
              key={i}
              className="line-clamp-2 text-[11px] text-ink-1 leading-tight"
            >
              {line}
            </p>
          ))}
        </div>
      ) : (
        <p className="text-[11px] text-ink-3 italic">{i18n._(msg`Vuota`)}</p>
      )}
    </button>
  );
};
SlideThumb.displayName = "PptxEditorInline.SlideThumb";

// ─── Active-slide editor ────────────────────────────────────────────────

const SlideEditor: React.FC<{
  slide: PptxSlideModel;
  onRunChange: (run: PptxTextRun, next: string) => void;
}> = ({ slide, onRunChange }) => {
  const { i18n } = useLingui();
  return (
  <div className="mx-auto w-full max-w-[680px] space-y-3">
    <header className="flex items-baseline justify-between border-hairline border-b pb-2">
      <h2 className="font-semibold text-[13px] text-ink-1">
        {i18n._(msg`Slide ${slide.index}`)}
      </h2>
      <span className="text-[10.5px] text-ink-3 tabular-nums">
        {i18n._(msg`${slide.runs.length} blocchi di testo`)}
      </span>
    </header>
    {slide.runs.length === 0 ? (
      <p className="text-[12px] text-ink-3">
        {i18n._(
          msg`Nessun testo modificabile in questa slide. Per inserire nuove forme o immagini, chiedi ad Ando.`,
        )}
      </p>
    ) : (
      <ul className="space-y-2">
        {slide.runs.map((run) => (
          <li key={run.id}>
            <RunTextarea run={run} onChange={onRunChange} />
          </li>
        ))}
      </ul>
    )}
  </div>
  );
};
SlideEditor.displayName = "PptxEditorInline.SlideEditor";

const RunTextarea: React.FC<{
  run: PptxTextRun;
  onChange: (run: PptxTextRun, next: string) => void;
}> = ({ run, onChange }) => {
  // Local mirror so React owns the input; the model mutation happens
  // inside onChange so the parent doesn't re-render the whole slide.
  const [value, setValue] = useState(run.text);
  // Reset when the slide swaps to a different run (key changes), so a
  // stale local value doesn't bleed into the next slide.
  useEffect(() => {
    setValue(run.text);
  }, [run]);
  return (
    <textarea
      value={value}
      onChange={(e) => {
        const next = e.target.value;
        setValue(next);
        onChange(run, next);
      }}
      rows={Math.min(6, Math.max(1, value.split("\n").length))}
      className={cn(
        "w-full resize-y rounded-md border border-hairline bg-white px-3 py-2",
        "text-[13px] text-ink-1 leading-snug",
        "focus-visible:border-ando-orange focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
      )}
      spellCheck
    />
  );
};
RunTextarea.displayName = "PptxEditorInline.RunTextarea";
